/**
 * JKL Find Store Drawer
 */

const DRAWER_SELECTOR = "body > x-drawer[data-find-store-drawer]";
const TRIGGER_SELECTOR = "[data-find-store-trigger]";
const PAYLOAD_SELECTOR = "script[data-find-store-trigger-payload]";
const APP_SELECTOR = "[data-find-store-app]";
const MAPS_SCRIPT_SELECTOR = "script[data-jkl-google-places-script]";
const LOOKUP_DEBOUNCE_MS = 280;
const BLUR_CLOSE_DELAY_MS = 150;
const STOCK_API_BRAND = "sisley";
const STOCK_API_COUNTRY_FALLBACK = "IT";
const MAPS_READY_TIMEOUT_MS = 5000;
const MAPS_READY_INTERVAL_MS = 50;

const LOOKUP_STATES = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  EMPTY: "empty",
  ERROR: "error",
  NOT_CONFIGURED: "not_configured",
};

const formSessions = new Map();
let mapsPromise = null;

function text(value) {
  return String(value ?? "").trim();
}

function optionAt(variant, position) {
  return text(variant?.options?.[Number(position) - 1]);
}

function joinText(parts, separator = ", ") {
  return parts.map(text).filter(Boolean).join(separator);
}

function getConfig() {
  const config = window.JKL_FIND_STORE_CONFIG || {};
  return {
    googleMapsApiKey: text(config.googleMapsApiKey),
    stockApiEndpoint: text(config.stockApiEndpoint),
  };
}

function getSession(formId) {
  if (!formSessions.has(formId)) {
    formSessions.set(
      formId,
      window.Vue.reactive({ hasExplicitSizeSelection: false }),
    );
  }

  return formSessions.get(formId);
}

function parsePayload(triggerEl) {
  try {
    const raw = JSON.parse(
      triggerEl.nextElementSibling.matches(PAYLOAD_SELECTOR)
        ? triggerEl.nextElementSibling.textContent || "{}"
        : "{}",
    );
    const variants = raw.variants.map((variant) => ({
      id: text(variant.id),
      sku: text(variant.sku),
      priceFormatted: text(variant.priceFormatted),
      lowestPrice30DaysFormatted: text(variant.lowestPrice30DaysFormatted),
      options: variant.options.map(text),
    }));

    return {
      sizeOptionPosition: Number(raw.optionMeta.sizeOptionPosition) || null,
      colorOptionPosition: Number(raw.optionMeta.colorOptionPosition) || null,
      sizeValues: raw.optionMeta.sizeValues.map(text),
      sizeValueToOptionValueId: raw.optionMeta.sizeValueToOptionValueId || {},
      variants,
      variantMap: new Map(variants.map((variant) => [variant.id, variant])),
    };
  } catch {
    return null;
  }
}

function getProductForm(formId) {
  return document.forms[formId] || document.getElementById(formId);
}

function currentVariantId(formId) {
  const form = getProductForm(formId);
  return text(
    form?.elements?.id?.value || form?.querySelector('input[name="id"]')?.value,
  );
}

function variantSignature(variant, sizePosition) {
  return variant.options
    .map((value, index) =>
      index === sizePosition - 1 ? null : `${index}:${text(value)}`,
    )
    .filter(Boolean)
    .join("|");
}

function selectFormSize(formId, sizePosition, optionValueId) {
  if (!optionValueId) return;

  const escapedFormId = window.CSS?.escape
    ? window.CSS.escape(formId)
    : formId.replace(/["\\]/g, "\\$&");
  const escapedValue = window.CSS?.escape
    ? window.CSS.escape(optionValueId)
    : optionValueId.replace(/["\\]/g, "\\$&");
  const selector = `input[data-option-position="${sizePosition}"][value="${escapedValue}"]`;
  const input =
    document.querySelector(`${selector}[form="${escapedFormId}"]`) ||
    getProductForm(formId)?.querySelector(selector);

  if (input instanceof HTMLInputElement) {
    input.checked = true;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }
}

function normalizeStore(deliveryPoint) {
  const address = deliveryPoint.location.addressComponents || {};
  const name = text(deliveryPoint.pointName);
  const addressLine = text(address.route);
  const locationLine = joinText([address.locality, address.country]);

  return {
    key: text(deliveryPoint.pointId) || joinText([name, addressLine], "|"),
    name,
    addressLine,
    locationLine,
    available: Number(deliveryPoint.quantity) > 0,
  };
}

async function lookupStockStores(endpoint, { lat, lng, sku, signal }) {
  const url = new URL(endpoint, window.location.origin);
  url.searchParams.set("brand", STOCK_API_BRAND);
  url.searchParams.set(
    "country",
    text(
      window.pageContext?.country || STOCK_API_COUNTRY_FALLBACK,
    ).toUpperCase(),
  );
  url.searchParams.set("lat", String(lat));
  url.searchParams.set("lng", String(lng));
  url.searchParams.set("sku", sku);

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: { Accept: "application/json" },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Stock API request failed with status ${response.status}.`);
  }

  const body = text(await response.text());
  if (!body) return [];

  const { deliveryPoints = [] } = JSON.parse(body);
  return Array.isArray(deliveryPoints)
    ? deliveryPoints.map(normalizeStore)
    : [];
}

function waitForMapsImportLibrary() {
  if (window.google?.maps?.importLibrary) {
    return Promise.resolve(window.google.maps);
  }

  const startTime = Date.now();

  return new Promise((resolve, reject) => {
    const checkReady = () => {
      if (window.google?.maps?.importLibrary) {
        resolve(window.google.maps);
        return;
      }

      if (Date.now() - startTime >= MAPS_READY_TIMEOUT_MS) {
        reject(new Error("Google Maps script did not expose importLibrary."));
        return;
      }

      window.setTimeout(checkReady, MAPS_READY_INTERVAL_MS);
    };

    checkReady();
  });
}

function loadGoogleMaps(apiKey) {
  if (window.google?.maps?.importLibrary) {
    return Promise.resolve(window.google.maps);
  }

  if (mapsPromise) {
    return mapsPromise;
  }

  mapsPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(MAPS_SCRIPT_SELECTOR);
    if (existingScript instanceof HTMLScriptElement) {
      waitForMapsImportLibrary().then(resolve, reject);
      existingScript.addEventListener(
        "load",
        () => waitForMapsImportLibrary().then(resolve, reject),
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.defer = true;
    script.dataset.jklGooglePlacesScript = "true";
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}` +
      "&v=weekly&loading=async";
    script.addEventListener(
      "load",
      () => waitForMapsImportLibrary().then(resolve, reject),
      { once: true },
    );
    script.addEventListener("error", () =>
      reject(new Error("Failed to load Google Maps script.")),
    );
    document.head.appendChild(script);
  }).catch((error) => {
    mapsPromise = null;
    throw error;
  });

  return mapsPromise;
}

function createPlacesClient(apiKey) {
  let placesPromise = null;
  let sessionToken = null;

  async function places() {
    placesPromise ||= loadGoogleMaps(apiKey).then(() =>
      window.google.maps.importLibrary("places"),
    );

    return placesPromise;
  }

  return {
    async getSuggestions(input) {
      const { AutocompleteSessionToken, AutocompleteSuggestion } =
        await places();
      sessionToken ||= new AutocompleteSessionToken();

      const { suggestions = [] } =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          sessionToken,
        });

      return suggestions.map(({ placePrediction }) => ({
        id: text(placePrediction.placeId),
        label:
          text(placePrediction.text?.text) ||
          joinText([
            placePrediction.mainText?.text,
            placePrediction.secondaryText?.text,
          ]),
        prediction: placePrediction,
      }));
    },

    async getCoordinates(suggestion) {
      const place = suggestion.prediction.toPlace();
      await place.fetchFields({ fields: ["location"] });
      sessionToken = null;

      return {
        lat: Number(place.location.lat()),
        lng: Number(place.location.lng()),
      };
    },

    resetSession() {
      sessionToken = null;
    },
  };
}

function getTemplate(appRoot) {
  appRoot.__jklFindStoreTemplate ||= appRoot.innerHTML;
  return appRoot.__jklFindStoreTemplate;
}

function cleanupDrawer(drawerEl) {
  drawerEl.__jklFindStoreApp?.unmount?.();

  const appRoot = drawerEl.querySelector(APP_SELECTOR);
  if (appRoot instanceof HTMLElement) {
    appRoot.innerHTML = getTemplate(appRoot);
    appRoot.setAttribute("v-cloak", "");
  }

  delete drawerEl.__jklFindStoreApp;
}

function createFindStoreApp({ formId, initialVariantId, payload, config }) {
  const { createApp, computed, onBeforeUnmount, onMounted, ref } = window.Vue;
  const form = getProductForm(formId);
  const placesClient = config.googleMapsApiKey
    ? createPlacesClient(config.googleMapsApiKey)
    : null;
  const stockEndpoint = text(config.stockApiEndpoint);

  return createApp({
    setup() {
      const selectedVariantId = ref(initialVariantId);
      const locationQuery = ref("");
      const suggestions = ref([]);
      const suggestionsOpen = ref(false);
      const stores = ref([]);
      const lookupState = ref(LOOKUP_STATES.IDLE);

      let blurTimerId = 0;
      let predictionTimerId = 0;
      let predictionRequestId = 0;
      let stockAbortController = null;

      const session = getSession(formId);
      const hasSizeOption = Boolean(
        payload.sizeOptionPosition && payload.sizeValues.length,
      );
      const sizeSelectionUnlocked = computed(
        () => !hasSizeOption || session.hasExplicitSizeSelection,
      );
      const selectedVariant = computed(
        () => payload.variantMap.get(text(selectedVariantId.value)) || null,
      );
      const headerPrice = computed(() =>
        text(selectedVariant.value?.priceFormatted),
      );
      const headerLowestPrice30d = computed(() =>
        text(selectedVariant.value?.lowestPrice30DaysFormatted),
      );
      const selectedColorValue = computed(() =>
        optionAt(selectedVariant.value, payload.colorOptionPosition),
      );
      const productCode = computed(() => text(selectedVariant.value?.sku));
      const showSuggestions = computed(
        () => suggestionsOpen.value && suggestions.value.length > 0,
      );
      const sizeChips = computed(() => {
        if (!hasSizeOption || !selectedVariant.value) return [];

        const signature = variantSignature(
          selectedVariant.value,
          payload.sizeOptionPosition,
        );
        const chips = payload.variants
          .filter(
            (variant) =>
              variantSignature(variant, payload.sizeOptionPosition) ===
              signature,
          )
          .map((variant) => {
            const label = optionAt(variant, payload.sizeOptionPosition);
            return {
              label,
              variantId: variant.id,
              optionValueId: payload.sizeValueToOptionValueId[label],
            };
          })
          .filter((chip, index, list) => {
            return (
              chip.label &&
              list.findIndex((item) => item.label === chip.label) === index
            );
          });

        return chips.sort(
          (a, b) =>
            payload.sizeValues.indexOf(a.label) -
            payload.sizeValues.indexOf(b.label),
        );
      });

      function resetLookup() {
        predictionRequestId += 1;
        window.clearTimeout(blurTimerId);
        window.clearTimeout(predictionTimerId);
        stockAbortController?.abort();
        stockAbortController = null;
        placesClient?.resetSession();
        locationQuery.value = "";
        suggestions.value = [];
        suggestionsOpen.value = false;
        stores.value = [];
        lookupState.value = LOOKUP_STATES.IDLE;
      }

      function setSelectedVariant(variantId) {
        const id = text(variantId);
        if (!payload.variantMap.has(id)) return false;
        selectedVariantId.value = id;
        return true;
      }

      function queuePredictionLookup() {
        window.clearTimeout(predictionTimerId);

        const query = text(locationQuery.value);
        if (!query) {
          predictionRequestId += 1;
          suggestions.value = [];
          suggestionsOpen.value = false;
          return;
        }

        predictionTimerId = window.setTimeout(async () => {
          if (!placesClient) {
            suggestions.value = [];
            lookupState.value = LOOKUP_STATES.NOT_CONFIGURED;
            return;
          }

          const requestId = ++predictionRequestId;

          try {
            const predictionItems = await placesClient.getSuggestions(query);
            if (requestId !== predictionRequestId) return;

            suggestions.value = predictionItems;
            suggestionsOpen.value = suggestions.value.length > 0;
          } catch (error) {
            if (requestId !== predictionRequestId) return;
            suggestions.value = [];
            suggestionsOpen.value = false;
            lookupState.value = LOOKUP_STATES.ERROR;
            console.error("Find Store autocomplete failed.", error);
          }
        }, LOOKUP_DEBOUNCE_MS);
      }

      function onLocationInput() {
        if (hasSizeOption && !sizeSelectionUnlocked.value) return;
        stockAbortController?.abort();
        lookupState.value = LOOKUP_STATES.IDLE;
        stores.value = [];
        suggestionsOpen.value = true;
        queuePredictionLookup();
      }

      function onLocationFocus() {
        if (hasSizeOption && !sizeSelectionUnlocked.value) return;
        suggestionsOpen.value = true;
        if (text(locationQuery.value)) queuePredictionLookup();
      }

      function onLocationBlur() {
        window.clearTimeout(blurTimerId);
        blurTimerId = window.setTimeout(() => {
          suggestionsOpen.value = false;
        }, BLUR_CLOSE_DELAY_MS);
      }

      async function selectSuggestion(suggestion) {
        window.clearTimeout(blurTimerId);
        window.clearTimeout(predictionTimerId);
        predictionRequestId += 1;

        locationQuery.value = text(suggestion.label);
        suggestions.value = [];
        suggestionsOpen.value = false;

        if (!placesClient || !stockEndpoint) {
          stores.value = [];
          lookupState.value = LOOKUP_STATES.NOT_CONFIGURED;
          return;
        }

        const sku = text(selectedVariant.value?.sku);
        if (!sku) {
          stores.value = [];
          lookupState.value = LOOKUP_STATES.ERROR;
          return;
        }

        lookupState.value = LOOKUP_STATES.LOADING;
        stores.value = [];

        try {
          const { lat, lng } = await placesClient.getCoordinates(suggestion);

          stockAbortController?.abort();
          stockAbortController = new AbortController();
          stores.value = await lookupStockStores(stockEndpoint, {
            lat,
            lng,
            sku,
            signal: stockAbortController.signal,
          });
          lookupState.value = stores.value.length
            ? LOOKUP_STATES.SUCCESS
            : LOOKUP_STATES.EMPTY;
        } catch (error) {
          if (error?.name === "AbortError") return;
          stores.value = [];
          lookupState.value = LOOKUP_STATES.ERROR;
          console.error("Find Store stock lookup failed.", error);
        }
      }

      function selectSize(chip) {
        session.hasExplicitSizeSelection = true;
        if (!setSelectedVariant(chip.variantId)) return;

        resetLookup();
        selectFormSize(
          formId,
          payload.sizeOptionPosition,
          text(chip.optionValueId),
        );
      }

      function handleVariantChange(event) {
        const eventFormId = text(event?.detail?.formId);
        if (eventFormId && eventFormId !== formId) return;

        const nextVariantId =
          text(event?.detail?.variant?.id) || currentVariantId(formId);
        if (setSelectedVariant(nextVariantId)) resetLookup();
      }

      onMounted(() => {
        setSelectedVariant(currentVariantId(formId));
        form?.addEventListener("variant:change", handleVariantChange);
      });

      onBeforeUnmount(() => {
        window.clearTimeout(blurTimerId);
        window.clearTimeout(predictionTimerId);
        stockAbortController?.abort();
        placesClient?.resetSession();
        form?.removeEventListener("variant:change", handleVariantChange);
      });

      return {
        selectedVariantId,
        sizeSelectionUnlocked,
        hasSizeOption,
        sizeChips,
        headerPrice,
        headerLowestPrice30d,
        selectedColorValue,
        productCode,
        locationQuery,
        suggestions,
        showSuggestions,
        stores,
        lookupState,
        selectSize,
        onLocationInput,
        onLocationFocus,
        onLocationBlur,
        selectSuggestion,
      };
    },
  });
}

function mountDrawerApp(drawerEl, triggerEl, appRoot) {
  try {
    const formId = text(triggerEl.dataset.findStoreFormId);
    const payload = parsePayload(triggerEl);
    const initialVariantId = currentVariantId(formId);

    if (
      !window.Vue?.createApp ||
      !formId ||
      !payload?.variantMap.has(initialVariantId)
    ) {
      throw new Error("Find Store drawer context is invalid.");
    }

    cleanupDrawer(drawerEl);

    const app = createFindStoreApp({
      formId,
      initialVariantId,
      payload,
      config: getConfig(),
    });
    app.mount(appRoot);
    drawerEl.__jklFindStoreApp = app;
  } catch (error) {
    cleanupDrawer(drawerEl);
    if (drawerEl.open && typeof drawerEl.hide === "function")
      void drawerEl.hide();
    console.error("Find Store open failed.", error);
  }
}

function initFindStoreDrawer() {
  const drawerEl = document.querySelector(DRAWER_SELECTOR);
  const triggerEl = document.querySelector(TRIGGER_SELECTOR);
  const appRoot = drawerEl?.querySelector(APP_SELECTOR);

  if (
    !(drawerEl instanceof HTMLElement) ||
    !(triggerEl instanceof HTMLElement) ||
    !(appRoot instanceof HTMLElement) ||
    drawerEl.__jklFindStoreBound
  ) {
    return;
  }

  getTemplate(appRoot);
  triggerEl.addEventListener("click", () => drawerEl.show());
  drawerEl.addEventListener("dialog:after-show", () =>
    mountDrawerApp(drawerEl, triggerEl, appRoot),
  );
  drawerEl.addEventListener("dialog:after-hide", () => cleanupDrawer(drawerEl));
  drawerEl.__jklFindStoreBound = true;
}

initFindStoreDrawer();
